import { useSelector } from "react-redux";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes
} from "react-router-dom";

import { ROUTE_PATHS } from "../utils/RoutesPaths";
import { getInitialRoute, PERMISSIONS_IDS } from "../utils/Roles";
// ========= Components =========
import AuthLayout from "../components/AuthLayout";
import Layout from "../components/Layout";
import ChangePassword from "../containers/Auth/ChangePassword";
import ForgetPassword from "../containers/Auth/ForgetPassword";
import Login from "../containers/Auth/Login";
import VerificationRequestsList from "../containers/BusinessVerification/VerificationRequests";
import ViewVerificationRequestDetails from "../containers/BusinessVerification/VerificationRequests/ViewRequestDetails";
import AddEditCategory from "../containers/Categories/AddEditCategory";
import ApprovedCategories from "../containers/Categories/ApprovedCategories";
import CategoryDetails from "../containers/Categories/CategoryDetails";
import CategoryRequestDetails from "../containers/Categories/CategoryRequestDetails";
import RequestsCategories from "../containers/Categories/RequestsCategories";
import AddEditEmployees from "../containers/Employees/AddEditEmployees";
import EmployeeDetails from "../containers/Employees/EmployeeDetails";
import EmployeesList from "../containers/Employees/EmployeesList";
import Notifications from "../containers/Notifications";
import ApprovedProducts from "../containers/Products/ApprovedProducts";
import ProductDetails from "../containers/Products/ProductDetails";
import ReviewRequests from "../containers/Products/ReviewRequests";
import RolesList from "../containers/Roles/RolesList";
import SubCategories from "../containers/SubCategories";
import AddEditSubCategory from "../containers/SubCategories/AddEditSubCategory";
import SubCategoryDetails from "../containers/SubCategories/SubCategoryDetails";
import Exporters from "../containers/Users/Exporters";
import ExporterDetails from "../containers/Users/Exporters/ExporterDetails";
import Importers from "../containers/Users/Importers";
import ImporterDetails from "../containers/Users/Importers/ImporterDetails";
import Combined from "../containers/Users/Combined";
import CombinedDetails from "../containers/Users/Combined/CombinedDetails";
import VouchersList from "../containers/Vouchers";
import TransactionsList from "../containers/Transactions";
import RoleDetails from "../containers/Roles/RoleDetails";
import ResetPassword from "../containers/Auth/ResetPassword";
import AddEditRole from "../containers/Roles/AddEditRole";
import NotFound from "../containers/NotFound";
import AuthorizedRoute from "./AuthorizedRoute";

const AppRoutes = () => {
  const isAuth = useSelector((state) => state.auth.login?.Token);
  const IsPasswordReset = useSelector(
    (state) => state.auth.login?.IsPasswordReset
  );

  return (
    <BrowserRouter>
      <Routes>
        <Route
          element={
            isAuth ? (
              <Navigate to={getInitialRoute()} />
            ) : (
              <AuthLayout />
            )
          }>
          <Route path={ROUTE_PATHS.login} element={<Login />} />
          <Route
            path={ROUTE_PATHS.forgetPassword}
            element={<ForgetPassword />}
          />
          <Route
            path={ROUTE_PATHS.changePassword}
            element={<ChangePassword />}
          />
        </Route>
        <Route
          element={
            isAuth && !IsPasswordReset ? (
              <AuthLayout />
            ) : (
              <Navigate to={getInitialRoute()} />
            )
          }>
          <Route
            path={ROUTE_PATHS.resetPassword}
            element={
              IsPasswordReset ? (
                <Navigate to={getInitialRoute()} />
              ) : (
                <ResetPassword />
              )
            }
          />
        </Route>
        <Route
          element={
            isAuth ? (
              IsPasswordReset ? (
                <Layout />
              ) : (
                <Navigate to={ROUTE_PATHS.resetPassword} />
              )
            ) : (
              <Navigate to={ROUTE_PATHS.login} />
            )
          }>
          {/* Products Requests Routes */}
          <Route
            element={
              <AuthorizedRoute
                pemissionIds={[PERMISSIONS_IDS.product_request_view]}
              />
            }>
            <Route
              path={ROUTE_PATHS.productsRequests}
              element={<ReviewRequests />}
            />
            <Route
              path={ROUTE_PATHS.productDetailsRequest}
              element={<ProductDetails />}
            />
          </Route>

          {/* Products Routes */}
          <Route
            element={
              <AuthorizedRoute
                pemissionIds={[PERMISSIONS_IDS.product_view]}
              />
            }>
            <Route
              path={ROUTE_PATHS.approvedProducts}
              element={<ApprovedProducts />}
            />
            <Route
              path={ROUTE_PATHS.productDetailsApproved}
              element={<ProductDetails />}
            />
          </Route>

          {/* Categories Requests Routes */}
          <Route
            element={
              <AuthorizedRoute
                pemissionIds={[PERMISSIONS_IDS.category_request_view]}
              />
            }>
            <Route
              path={ROUTE_PATHS.categoriesRequests}
              element={<RequestsCategories />}
            />
            <Route
              path={ROUTE_PATHS.categoriesRequestsDetails}
              element={<CategoryRequestDetails />}
            />
          </Route>

          {/* Categories Routes */}
          <Route
            element={
              <AuthorizedRoute
                pemissionIds={[PERMISSIONS_IDS.category_view]}
              />
            }>
            <Route
              path={ROUTE_PATHS.approvedCategories}
              element={<ApprovedCategories />}
            />
            <Route
              path={ROUTE_PATHS.categoryDetailsApproved}
              element={<CategoryDetails />}
            />
            <Route
              element={
                <AuthorizedRoute
                  pemissionIds={[PERMISSIONS_IDS.category_add]}
                />
              }>
              <Route
                path={ROUTE_PATHS.addCategory}
                element={<AddEditCategory />}
              />
            </Route>
            <Route
              element={
                <AuthorizedRoute
                  pemissionIds={[PERMISSIONS_IDS.category_edit]}
                />
              }>
              <Route
                path={ROUTE_PATHS.editCategory}
                element={<AddEditCategory />}
              />
            </Route>
          </Route>

          {/* Sub Categories Routes */}
          <Route
            element={
              <AuthorizedRoute
                pemissionIds={[PERMISSIONS_IDS.sub_category_view]}
              />
            }>
            <Route
              path={ROUTE_PATHS.subCategories}
              element={<SubCategories />}
            />
            <Route
              path={ROUTE_PATHS.subcategoryDetails}
              element={<SubCategoryDetails />}
            />
            <Route
              element={
                <AuthorizedRoute
                  pemissionIds={[PERMISSIONS_IDS.sub_category_add]}
                />
              }>
              <Route
                path={ROUTE_PATHS.addSubCategories}
                element={<AddEditSubCategory />}
              />
            </Route>
            <Route
              element={
                <AuthorizedRoute
                  pemissionIds={[PERMISSIONS_IDS.sub_category_edit]}
                />
              }>
              <Route
                path={ROUTE_PATHS.editSubCategories}
                element={<AddEditSubCategory />}
              />
            </Route>
          </Route>

          {/* Importers Routes */}
          <Route
            element={
              <AuthorizedRoute
                pemissionIds={[PERMISSIONS_IDS.users_importer_view]}
              />
            }>
            <Route
              path={ROUTE_PATHS.usersImporters}
              element={<Importers />}
            />
            <Route
              path={ROUTE_PATHS.usersImporterDetails}
              element={<ImporterDetails />}
            />
          </Route>

          {/* Exporters Routes */}
          <Route
            element={
              <AuthorizedRoute
                pemissionIds={[PERMISSIONS_IDS.users_exporter_view]}
              />
            }>
            <Route
              path={ROUTE_PATHS.usersExporters}
              element={<Exporters />}
            />
            <Route
              path={ROUTE_PATHS.usersExporterDetails}
              element={<ExporterDetails />}
            />
            <Route
              path={ROUTE_PATHS.usersCombined}
              element={<Combined />}
            />
            <Route
              path={ROUTE_PATHS.usersCombinedDetails}
              element={<CombinedDetails />}
            />
          </Route>

          {/* Verification Routes */}
          <Route
            element={
              <AuthorizedRoute
                pemissionIds={[
                  PERMISSIONS_IDS.verification_request_offplatform_view,
                  PERMISSIONS_IDS.verification_request_online_view,
                  PERMISSIONS_IDS.verification_request_banktransfer_view
                ]}
              />
            }>
            <Route
              path={ROUTE_PATHS.verificationRequests}
              element={<VerificationRequestsList />}
            />
            <Route
              path={ROUTE_PATHS.verificationRequestDetails}
              element={<ViewVerificationRequestDetails />}
            />
          </Route>

          {/* Vouchers Routes */}
          <Route
            element={
              <AuthorizedRoute
                pemissionIds={[PERMISSIONS_IDS.vouchers_view]}
              />
            }>
            <Route
              path={ROUTE_PATHS.vouchers}
              element={<VouchersList />}
            />
          </Route>

          {/* Roles Routes */}
          <Route
            element={
              <AuthorizedRoute
                pemissionIds={[PERMISSIONS_IDS.roles_view]}
              />
            }>
            <Route path={ROUTE_PATHS.roles} element={<RolesList />} />
            <Route
              path={ROUTE_PATHS.roleDetails}
              element={<RoleDetails />}
            />
            <Route
              element={
                <AuthorizedRoute
                  pemissionIds={[PERMISSIONS_IDS.roles_add]}
                />
              }>
              <Route
                path={ROUTE_PATHS.addRole}
                element={<AddEditRole />}
              />
            </Route>
            <Route
              element={
                <AuthorizedRoute
                  pemissionIds={[PERMISSIONS_IDS.roles_edit]}
                />
              }>
              <Route
                path={ROUTE_PATHS.editRole}
                element={<AddEditRole />}
              />
            </Route>
          </Route>

          {/* Employees Routes */}
          <Route
            element={
              <AuthorizedRoute
                pemissionIds={[PERMISSIONS_IDS.employees_view]}
              />
            }>
            <Route
              path={ROUTE_PATHS.employeesList}
              element={<EmployeesList />}
            />
            <Route
              path={ROUTE_PATHS.employeeDetails}
              element={<EmployeeDetails />}
            />
            <Route
              element={
                <AuthorizedRoute
                  pemissionIds={[PERMISSIONS_IDS.employees_add]}
                />
              }>
              <Route
                path={ROUTE_PATHS.addEmployees}
                element={<AddEditEmployees />}
              />
            </Route>
            <Route
              element={
                <AuthorizedRoute
                  pemissionIds={[PERMISSIONS_IDS.employees_edit]}
                />
              }>
              <Route
                path={ROUTE_PATHS.editEmployee}
                element={<AddEditEmployees />}
              />
            </Route>
          </Route>

          {/* Transactions Routes */}
          <Route
            element={
              <AuthorizedRoute
                pemissionIds={[PERMISSIONS_IDS.transactions_view]}
              />
            }>
            <Route
              path={ROUTE_PATHS.transactions}
              element={<TransactionsList />}
            />
          </Route>
          <Route
            exact
            path={ROUTE_PATHS.notifications}
            element={<Notifications />}
          />
          <Route path={ROUTE_PATHS.notFound} element={<NotFound />} />
        </Route>
        <Route
          path={ROUTE_PATHS.root}
          element={<Navigate to={getInitialRoute()} />}
        />
        <Route
          path={"*"}
          element={<Navigate to={getInitialRoute()} />}
        />
      </Routes>
    </BrowserRouter>
  );
};

export default AppRoutes;
